import AddCircle from '@mui/icons-material/AddCircle';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Chip, CircularProgress, List, ListItem, ListItemText, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Pagination from '@mui/material/Pagination';
import React, { SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CLOSED, CREATE_SURVEY, DRAFT, PUBLISHED, REOPEN, UPDATE_SURVEY, verticalScrollStyle } from '../../constants';
import socketHandler from '../../helpers/socket';
import {
  compareData,
  generateRandomString,
  getSurveyItem,
  handleActiveSection,
  handleOnScroll,
  sanitizeChapters,
  swapItems,
  validateSurveyForm,
} from '../../helpers/surveyUtil';
import { ChapterProps, SectionProps } from '../../models/global';
import { State } from '../../models/state';
import { getCodes } from '../../store/actions/code';
import {
  cloneSurvey,
  closeSurvey,
  createSurvey,
  getStaticValues,
  getSurvey,
  getSurveySeriesById,
  publishSurvey,
  reopenSurvey,
  updateSurvey,
} from '../../store/actions/survey';
import Confirmation from '../Confirmation/Confirmation';
import ButtonComponent from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import TextField from '../FormComponents/TextField';
import NoData from '../NoData/NoData';
import ToastMessage from '../ToastMessage/ToastMessage';
import TOCButton from '../TOCButton/TOCButton';
import SaveOrResetChanges from '../UserSurvey/SaveOrResetChanges';
import Actions from './Actions';
import Email from './Email';
import InterrelatedField, { InterrelatedFieldRef } from './InterrelatedField';
import Notes from './Notes';
import Section from './Section';
import './SurveyForm.scss';

interface SurveyFormProps {
  type: typeof CREATE_SURVEY | typeof UPDATE_SURVEY;
}

const SurveyForm = React.memo((props: SurveyFormProps) => {
  const { type } = props;
  const publishErrorMessage = 'You have some mandatory fields empty in English, please fill them in order to publish.';
  const { id, seriesId } = useParams();
  const modalRef = useRef<InterrelatedFieldRef>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { languages, surveySeries } = useSelector((state: State) => state.survey);
  const data = [
    {
      id: generateRandomString(true),
      name: 'Chapter 1',
      sections: [
        {
          id: generateRandomString(true),
          name: 'Section 1',
          fieldBlocks: [
            {
              id: generateRandomString(true),
              question: '',
              fieldType: '',
              codeId: null,
            },
          ],
        },
      ],
      active: true,
    },
  ];
  const initialChapterData = useRef(data);
  const [DBData, setDBData] = useState([]);
  const chaptersRef = useRef(JSON.parse(JSON.stringify(data)));
  const [surveyName, setSurveyName] = useState({});
  const [DBSurveyName, setDBSurveyName] = useState({});
  const [formClicked, setFormClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [cloneLoading, setCloneLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [invalidFields, setInvalidFields] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [language, setLanguage] = useState('English');
  const [emailPopup, setEmailPopup] = useState(false);
  const TOCClickedRef = useRef(false);
  const [email, setEmail] = useState({});
  const [emailSubject, setEmailSubject] = useState('');
  const [DBEmail, setDBEmail] = useState({});
  const [status, setStatus] = useState('');
  const [confirmationPopupText, setConfirmationPopupText] = useState('');
  const [confirmationType, setConfirmationType] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [currentChapterName, setCurrentChapterName] = useState(data[0].name);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [isSameData, setIsSameData] = useState(true);
  const [showToc, setShowToc] = useState(true);
  const [notesPopup, setNotesPopup] = useState(false);
  const [notes, setNotes] = useState('');
  const timer = useRef(null);

  useEffect(() => {
    if (DBData?.length && !timer.current) {
      timer.current = setInterval(() => {
        setIsSameData(
          compareData(
            sanitizeChapters(JSON.parse(JSON.stringify(DBData))),
            sanitizeChapters(JSON.parse(JSON.stringify(chaptersRef.current))),
          ) &&
            compareData(DBEmail, email) &&
            compareData(DBSurveyName, surveyName),
        );
      }, 10000);
    }

    return () => {
      clearInterval(timer.current);
      timer.current = null;
    };
  }, [DBData, DBEmail, DBSurveyName, email, surveyName]);

  const defaultData = compareData(initialChapterData.current, chaptersRef.current);
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      ((DBData.length && !isSameData) || (type === CREATE_SURVEY && !defaultData && !formClicked)) &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    const handleOnline = () => {
      setToastType('success');
      setToast('You are now online.');
      socketHandler();
    };
    const handleOffline = () => {
      setToastType('error');
      setToast('You are offline.');
    };
    const onPageLeave = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeunload', onPageLeave);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('beforeunload', onPageLeave);
    };
  }, []);

  useEffect(() => {
    if (formClicked) {
      const invalidFields = validateSurveyForm(chaptersRef.current, 'English');
      setInvalidFields(invalidFields);
    }
  }, [chaptersRef.current, language]);

  const closeConfirmationPopup = () => {
    setConfirmationPopupText('');
    setSendEmail(false);
  };

  const getSurveyData = () => {
    setPageLoading(true);
    dispatch(
      getSurvey(id, res => {
        setPageLoading(false);
        if (!res) {
          setNotFound(true);
        } else if (res?.chapters.length) {
          res.chapters[0].active = true;
          chaptersRef.current = res.chapters;
          setDBData(JSON.parse(JSON.stringify(res.chapters)));
          setCurrentChapterName(res.chapters[0][language === 'English' ? 'name' : `name${language}`]);
          setSurveyName({
            name: res.name,
            nameArabic: res.nameArabic,
            nameChinese: res.nameChinese,
            nameFrench: res.nameFrench,
            nameRussian: res.nameRussian,
            nameSpanish: res.nameSpanish,
          });
          setDBSurveyName({
            name: res.name,
            nameArabic: res.nameArabic,
            nameChinese: res.nameChinese,
            nameFrench: res.nameFrench,
            nameRussian: res.nameRussian,
            nameSpanish: res.nameSpanish,
          });
          setEmail({
            English: res.email?.english,
            Arabic: res.email?.arabic,
            Chinese: res.email?.chinese,
            French: res.email?.french,
            Russian: res.email?.russian,
            Spanish: res.email?.spanish,
          });
          setDBEmail({
            English: res.email?.english,
            Arabic: res.email?.arabic,
            Chinese: res.email?.chinese,
            French: res.email?.french,
            Russian: res.email?.russian,
            Spanish: res.email?.spanish,
          });
          setStatus(res.status);
          setNotes(res.notes);
          setEmailSubject(res.email?.subject || '');
        }
      }),
    );
  };

  useEffect(() => {
    dispatch(getSurveySeriesById(seriesId));
    if (type === UPDATE_SURVEY) {
      if (location?.state?.message) {
        setToastType('success');
        setToast(location?.state?.message);
        window.history.replaceState({}, '');
      }
      getSurveyData();
    }
  }, [id]);

  useEffect(() => {
    const survey = document.getElementById('survey');
    const scrollListener = () => {
      if (!TOCClickedRef.current) {
        const chapterData = handleActiveSection([...chaptersRef.current]);
        if (chapterData?.length) {
          chaptersRef.current = [...chapterData];
        }
      }
    };
    survey?.addEventListener('scroll', scrollListener);

    return () => {
      survey?.removeEventListener('scroll', scrollListener);
    };
  }, []);

  useEffect(() => {
    dispatch(getStaticValues());
    dispatch(getCodes());
  }, []);

  const addSection = (chapterId: string) => {
    const chapter = getSurveyItem(chaptersRef.current, chapterId) as ChapterProps;
    chapter.sections.push({
      name: `Section ${chapter.sections.length + 1}`,
      fieldBlocks: [
        {
          question: '',
          fieldType: '',
          id: generateRandomString(true),
          codeId: null,
          order: 1,
        },
      ],
      id: generateRandomString(true),
      order: chapter.sections.length + 1,
    });
    setForceUpdate(prev => !prev);
  };

  const addChapter = () => {
    const activeChapter = chaptersRef.current.find(el => el.active);
    if (activeChapter) {
      activeChapter.active = false;
    }
    const chapterName = `Chapter ${chaptersRef.current.length + 1}`;
    chaptersRef.current.push({
      name: chapterName,
      sections: [
        {
          name: 'Section 1',
          fieldBlocks: [
            {
              question: '',
              fieldType: '',
              id: generateRandomString(true),
              codeId: null,
              order: 1,
            },
          ],
          id: generateRandomString(true),
          order: 1,
        },
      ],
      id: generateRandomString(true),
      active: true,
      order: chaptersRef.current.length + 1,
    });
    setCurrentChapterName(chapterName);
  };

  const removeChapter = (chapterId: string) => {
    const chapterIndex = chaptersRef.current.findIndex(el => el.id === chapterId);

    chaptersRef.current = chaptersRef.current.filter(el => el.id !== chapterId);
    const newActiveChapterIndex = chapterIndex !== 0 ? chapterIndex - 1 : chapterIndex;

    let order = 1;
    chaptersRef.current.forEach(chapter => {
      chapter.order = order;
      order++;
    });

    if (chaptersRef.current?.length) {
      chaptersRef.current[newActiveChapterIndex].active = true;
      setCurrentChapterName(
        chaptersRef.current[newActiveChapterIndex][language === 'English' ? 'name' : `name${language}`],
      );
    } else {
      setForceUpdate(prev => !prev);
    }
  };

  const handleChapterTitle = (value: string, chapterId: string) => {
    const chapter = chaptersRef.current.find(el => el.id === chapterId);

    const key = language === 'English' ? 'name' : `name${language}`;
    chapter[key] = value;
    setCurrentChapterName(value);
  };

  const swapChapters = (chapterIndex: number, direction: 'prev' | 'next') => {
    swapItems(chaptersRef.current, chapterIndex, direction);
    setForceUpdate(prev => !prev);
  };

  const handleScrollOnClick = (chapterId: string, sectionId?: string) => {
    TOCClickedRef.current = true;
    const { idToScrollTo, chapters } = handleOnScroll(chapterId, [...chaptersRef.current], sectionId);

    const activeChapter = chapters.find(el => el.active);
    setCurrentChapterName(activeChapter[language === 'English' ? 'name' : `name${language}`]);

    setTimeout(() => {
      const elTOC = document.getElementById(`TOC_${idToScrollTo}`);
      if (elTOC) {
        elTOC.scrollIntoView({
          behavior: 'smooth',
        });
      }
    }, 10);

    setTimeout(() => {
      const el = document.getElementById(idToScrollTo);
      // const position = el.getBoundingClientRect().top;

      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
        });
        // window.scrollTo({
        //   behavior: 'smooth',
        //   top: position + window.scrollY - (headerHeight + 6),
        // });
      }
    }, 500);

    setTimeout(() => {
      TOCClickedRef.current = false;
    }, 1000);
  };

  const saveAsDraft = (callback?: () => void) => {
    setFormClicked(true);
    setLoading(true);
    const invalidFields = validateSurveyForm(chaptersRef.current, 'English');
    setInvalidFields(invalidFields);
    if (type === CREATE_SURVEY) {
      dispatch(
        createSurvey(
          {
            ...surveyName,
            email,
            emailSubject,
            surveySeriesId: seriesId,
            chapters: chaptersRef.current,
          },
          (res, message) => {
            if (res) {
              if (callback) {
                setLoading(false);
                setToastType('success');
                setToast(`Survey is created as draft. ${invalidFields?.length ? publishErrorMessage : ''}`);
                setTimeout(() => {
                  callback();
                }, 1000);
              } else {
                setTimeout(
                  () => {
                    setLoading(false);
                    navigate(`/survey-series/${seriesId}/survey/${res.id}`, {
                      state: {
                        message: `Survey is created as draft. ${invalidFields?.length ? publishErrorMessage : ''}`,
                      },
                    });
                  },
                  invalidFields.length ? 1000 : 0,
                );
              }
            } else {
              setLoading(false);
              setToastType('error');
              setToast(message || 'Opps, unknown error occured.');
            }
          },
        ),
      );
    } else {
      dispatch(
        updateSurvey(
          {
            data: {
              ...surveyName,
              email,
              emailSubject,
              surveySeriesId: seriesId,
              chapters: chaptersRef.current,
            },
            id,
          },
          (res, message) => {
            setLoading(false);
            if (res) {
              setToastType('success');
              setToast('Survey is updated.');
              getSurveyData();
              setIsSameData(true);
              if (callback) {
                setTimeout(() => {
                  callback();
                }, 1000);
              }
            } else {
              setToastType('error');
              setToast(message || 'Opps, unknown error occured.');
            }
          },
        ),
      );
    }
  };

  const handlePublish = () => {
    setConfirmLoading(true);
    dispatch(
      publishSurvey({ id, sendEmail }, (res, message) => {
        setConfirmLoading(false);
        closeConfirmationPopup();
        if (res) {
          setToastType('success');
          setToast(res.message || 'Survey is published.');
          setStatus(res.publishedSurvey.status);
        } else {
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  const handleClone = () => {
    setFormClicked(true);
    setCloneLoading(true);
    const invalidFields = validateSurveyForm(chaptersRef.current, 'English');
    setInvalidFields(invalidFields);
    dispatch(
      cloneSurvey(id, (res, message) => {
        if (res) {
          setTimeout(
            () => {
              setCloneLoading(false);
              navigate(`/survey-series/${seriesId}/survey/${res.id}`, {
                state: {
                  message: `Survey is cloned. ${invalidFields?.length ? publishErrorMessage : ''}`,
                },
              });
            },
            invalidFields.length ? 1000 : 0,
          );
        } else {
          setCloneLoading(false);
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  const handleClose = () => {
    setConfirmLoading(true);
    dispatch(
      closeSurvey(id, (res, message) => {
        setConfirmLoading(false);
        closeConfirmationPopup();
        if (res) {
          setToastType('success');
          setToast('Survey is closed.');
          setStatus(res.closedSurvey.status);
        } else {
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  const handleReopen = () => {
    setConfirmLoading(true);
    dispatch(
      reopenSurvey({ id, sendEmail }, (res, message) => {
        setConfirmLoading(false);
        closeConfirmationPopup();
        if (res) {
          setToastType('success');
          setToast('Survey is reopened.');
          setStatus(res.reopenedSurvey.status);
        } else {
          setToastType('error');
          setToast(message || 'Opps, unknown error occured.');
        }
      }),
    );
  };

  const getActiveChapterIndex = () => {
    const chapterIndex = chaptersRef.current.findIndex(el => el.active);
    return chapterIndex;
  };

  const handleLanguage = (lang: string) => {
    setLanguage(lang);
  };

  const handlePublishClick = () => {
    setFormClicked(true);
    const invalidFields = validateSurveyForm(chaptersRef.current, 'English');
    setInvalidFields(invalidFields);
    if (!surveyName['name'] || !email['English'] || invalidFields.length || !emailSubject) {
      setToastType('error');
      setToast(publishErrorMessage);
      return;
    }
    setConfirmationType(PUBLISHED);
    setConfirmationPopupText('Are you sure you want to publish this survey?');
  };

  const hasInvalidField = (chapterId: string, sectionId: string) => {
    const section = getSurveyItem(chaptersRef.current, chapterId, sectionId) as SectionProps;
    const invalidFieldBlockIds = invalidFields.map(invalidField =>
      invalidField.substring(invalidField.lastIndexOf('_') + 1),
    );
    return (
      formClicked &&
      language === 'English' &&
      (section.fieldBlocks.some(fieldBlock => invalidFieldBlockIds.includes(fieldBlock.id)) || !section.name)
    );
  };

  const unlockBlock = () => {
    if (blocker?.state === 'blocked') {
      blocker.proceed();
    }
  };

  const forceToUpdate = useCallback(() => setForceUpdate(prev => !prev), [forceUpdate]);

  const readonly = status === CLOSED || status === PUBLISHED;

  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }
  if (type === UPDATE_SURVEY && notFound) {
    return <NoData message='No survey found' />;
  }
  return (
    <Box className='survey-container'>
      <Box>
        <Box display='flex' marginBottom={4}>
          {type === UPDATE_SURVEY && (
            <ButtonComponent text='Add notes' size='small' variant='outlined' onClick={() => setNotesPopup(true)} />
          )}
          <Typography variant='h5' textAlign='center' className='title'>
            Survey {type === CREATE_SURVEY ? 'creation' : 'details'}
          </Typography>
          {!!status && (
            <Box marginLeft='auto'>
              <Chip
                label={status}
                color={status === DRAFT ? 'primary' : status === PUBLISHED ? 'success' : 'error'}
                sx={{ width: 110, fontSize: 15 }}
              />
            </Box>
          )}
        </Box>
        <Typography variant='body1' textAlign='left' marginBottom={2}>
          Series: {surveySeries?.name}
        </Typography>
        <Box className='survey-top-container'>
          <Box style={readonly ? { pointerEvents: 'none' } : {}}>
            <TextField
              label='Survey name'
              id='survey-title'
              variant='outlined'
              className='survey-name'
              value={surveyName[language === 'English' ? 'name' : `name${language}`] || ''}
              onChange={e =>
                setSurveyName({
                  ...surveyName,
                  [language === 'English' ? 'name' : `name${language}`]: e.target.value,
                })
              }
              required
              error={!surveyName['name'] && formClicked && language === 'English'}
              maxLength={400}
            />
            {language !== 'English' && (
              <Typography className='english-value '>English: {surveyName['name'] || 'N/A'}</Typography>
            )}
          </Box>
          <Dropdown
            value={language}
            label='Language'
            options={languages}
            onChange={(_: SyntheticEvent, value: string) => handleLanguage(value)}
            disableClearable
          />
        </Box>
      </Box>
      <Typography
        variant='body1'
        textAlign='left'
        color={((!email['English'] && language === 'English') || !emailSubject) && formClicked ? 'red' : '#1976d2'}
        sx={{ mt: 1, cursor: 'pointer', width: '140px', pointerEvents: 'auto' }}
        onClick={() => setEmailPopup(true)}
      >
        Configure email
      </Typography>
      <Box className='survey-inner-container'>
        {showToc && (
          <Box className='toc-container'>
            <Typography className='toc-heading'>TOC</Typography>
            <List className='toc-list' sx={verticalScrollStyle}>
              {chaptersRef.current.map(chapter => (
                <Box key={chapter.id}>
                  <ListItem>
                    <ListItemText
                      id={`TOC_${chapter.id}`}
                      className='toc-item-text'
                      primary={
                        <Tooltip title={chapter.name} placement='top-start'>
                          <Typography
                            variant='body1'
                            noWrap
                            onClick={() => handleScrollOnClick(chapter.id)}
                            sx={chapter.active ? { fontWeight: 'bold' } : {}}
                            display='inline'
                          >
                            {type === UPDATE_SURVEY && chapter.order}{' '}
                            {language === 'English'
                              ? chapter.name || 'N/A'
                              : `${chapter[`name${language}`] || ''}` || 'N/A'}
                          </Typography>
                        </Tooltip>
                      }
                    />
                  </ListItem>
                  <List sx={{ mb: 2.5 }}>
                    {chapter.sections.map(section => (
                      <ListItem key={section.id}>
                        <ListItemText
                          id={`TOC_${section.id}`}
                          className='toc-item-text'
                          primary={
                            <Tooltip title={section.name} placement='top-start'>
                              <Typography
                                variant='caption'
                                noWrap
                                onClick={() => handleScrollOnClick(chapter.id, section.id)}
                                sx={{
                                  fontWeight: chapter.active && section.active ? 'bold' : 'unset',
                                  marginLeft: '20px',
                                  color: hasInvalidField(chapter.id, section.id) ? 'red' : 'unset',
                                }}
                              >
                                {type === UPDATE_SURVEY && `${chapter.order}.${section.order} `}
                                {language === 'English'
                                  ? section.name || 'N/A'
                                  : `${section[`name${language}`] || ''}` || 'N/A'}
                              </Typography>
                            </Tooltip>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </List>
          </Box>
        )}
        <Box
          className='survey'
          id='survey'
          sx={verticalScrollStyle}
          style={{
            cursor: readonly ? 'not-allowed' : 'pointer',
          }}
        >
          <Box
            className='plus-minus-item'
            onClick={() => addChapter()}
            style={readonly ? { pointerEvents: 'none' } : {}}
          >
            <span>Add chapter</span>
            <AddCircle htmlColor='green' />
          </Box>
          {chaptersRef.current
            .filter(el => el.active)
            .map(chapter => (
              <Card
                key={chapter.id}
                className='chapter'
                id={chapter.id}
                style={readonly ? { pointerEvents: 'none' } : {}}
              >
                <Box sx={{ mb: 2.5, width: '100%' }} display='flex'>
                  <Box sx={{ width: '80%' }}>
                    {type === UPDATE_SURVEY && (
                      <Typography display='inline' fontWeight={700}>
                        {chapter.order}
                      </Typography>
                    )}
                    <TextField
                      id='chapter-name'
                      variant='standard'
                      className='name-field'
                      value={currentChapterName}
                      onChange={e => handleChapterTitle(e.target.value, chapter.id)}
                      error={invalidFields.includes(`CHAPTER_${chapter.id}`) && language === 'English'}
                      required
                      placeholder='Chapter name'
                      maxLength={600}
                    />
                    {language !== 'English' && (
                      <Typography className='english-value '>English: {chapter.name || 'N/A'}</Typography>
                    )}
                  </Box>

                  <Box sx={{ mt: 0.25, ml: 0.5 }}>
                    {getActiveChapterIndex() !== 0 && (
                      <RemoveCircleIcon htmlColor='red' onClick={() => removeChapter(chapter.id)} />
                    )}
                    {getActiveChapterIndex() !== chaptersRef.current.length - 1 && (
                      <Tooltip title='Swap with the next one'>
                        <ArrowDownwardIcon
                          fontSize='small'
                          sx={{ mb: 0.25 }}
                          onClick={() => swapChapters(getActiveChapterIndex(), 'next')}
                        />
                      </Tooltip>
                    )}
                    {getActiveChapterIndex() !== 0 && (
                      <Tooltip title='Swap with the previous one'>
                        <ArrowUpwardIcon
                          fontSize='small'
                          sx={{ mb: 0.25 }}
                          onClick={() => swapChapters(getActiveChapterIndex(), 'prev')}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box className='plus-minus-item' onClick={() => addSection(chapter.id)} style={{ marginLeft: '20px' }}>
                  <span>Add section</span> {<AddCircle htmlColor='green' />}
                </Box>

                {chapter.sections.map((section, sectionIndex) => (
                  <Section
                    key={sectionIndex}
                    section={section}
                    chapter={chapter}
                    sectionIndex={sectionIndex}
                    language={language}
                    modalRef={modalRef}
                    invalidFields={invalidFields}
                    chapters={chaptersRef.current}
                    readonly={readonly}
                    type={type}
                    forceToUpdate={forceToUpdate}
                  />
                ))}
              </Card>
            ))}
        </Box>
      </Box>
      <Box className='pagination-container'>
        <Pagination
          count={chaptersRef.current.length}
          onChange={(_, page) => handleScrollOnClick(chaptersRef.current[page - 1].id)}
          page={getActiveChapterIndex() + 1}
        />
        <TOCButton
          showToc={showToc}
          onClick={() =>
            requestAnimationFrame(() => {
              setShowToc(prev => !prev);
            })
          }
        />
      </Box>
      <Actions
        saveAsDraft={saveAsDraft}
        loading={loading}
        type={type}
        setConfirmationType={setConfirmationType}
        setConfirmationPopupText={setConfirmationPopupText}
        handleClone={handleClone}
        cloneLoading={cloneLoading}
        isSameData={isSameData}
        status={status}
        handlePublishClick={handlePublishClick}
        surveyId={id}
      />
      <ToastMessage
        toast={toast}
        close={() => setToast('')}
        messageType={toastType || 'success'}
        autoHideDuration={12000}
      />
      <InterrelatedField
        ref={modalRef}
        chapters={chaptersRef.current}
        language={language}
        forceToUpdate={forceToUpdate}
      />
      <Email
        open={emailPopup}
        done={({ emailContent, emailSubject }) => {
          setEmail({ ...email, [language]: emailContent });
          setEmailSubject(emailSubject);
        }}
        content={email[language]}
        defaultContent={email['English']}
        subject={emailSubject}
        language={language}
        close={() => setEmailPopup(false)}
        error={!email['English'] && formClicked && language === 'English'}
        errorEmailSubject={!emailSubject && formClicked}
        readonly={readonly}
        surveyLink={id ? `${window.location.origin}/survey-series/${seriesId}/survey/${id}` : null}
      />
      <Confirmation
        confirm={
          confirmationType === PUBLISHED ? handlePublish : confirmationType === CLOSED ? handleClose : handleReopen
        }
        text={confirmationPopupText}
        close={closeConfirmationPopup}
        loading={confirmLoading}
        additionalInfo={
          [PUBLISHED, REOPEN].includes(confirmationType) && (
            <Box display='flex' alignItems='center'>
              <Typography variant='body2'>Send notification email</Typography>
              <Checkbox
                checked={sendEmail}
                sx={{ paddingLeft: 0.5 }}
                size='small'
                onChange={() => setSendEmail(prev => !prev)}
              />
            </Box>
          )
        }
      />
      <SaveOrResetChanges
        open={blocker?.state === 'blocked' && !toast}
        close={() => {
          if (loading) {
            return;
          }
          if (blocker?.state === 'blocked') {
            blocker.reset();
          }
        }}
        language={language}
        save={() => {
          saveAsDraft(unlockBlock);
        }}
        discard={() => {
          if (loading) {
            return;
          }
          unlockBlock();
        }}
        loading={loading}
      />
      <Notes open={notesPopup} close={() => setNotesPopup(false)} noteData={notes} surveyId={id} />
    </Box>
  );
});

SurveyForm.displayName = 'SurveyForm';

export default SurveyForm;
